import type { DesignSystemIcons } from "design-system/types";

import type DateRange from "shared/helpers/DateRange";
import {
  ChartVariant,
  SovMetricType,
  TopArticlesMetric,
} from "shared/modules/Charts/types/chartSettings";
import type {
  InstantInsightsChart,
  SocialAnalyseChart,
} from "shared/modules/Charts/types/chartTypes";
import { AppSection } from "shared/types";
import { type FeatureKey } from "shared/types/features";
import type { MentionType, SocialMentionType } from "shared/types/mentions";

export type InsightsDashboardTemplateChartKey =
  | InstantInsightsChart
  | SocialAnalyseChart;

// Default settings that can be applied when creating a chart from a template
export interface InsightsDashboardTemplateChartDefaults {
  chart_variant?: ChartVariant;
  // Override the chart label in the template modal (i18n key)
  label_key?: string;
  sort_by?: TopArticlesMetric;
  sov_metric_type?: SovMetricType;
}

// A chart entry can be either a simple chart key or an object with defaults
export interface InsightsDashboardTemplateChartWithDefaults {
  chart: InsightsDashboardTemplateChartKey;
  defaults: InsightsDashboardTemplateChartDefaults;
}

export type InsightsDashboardTemplateChart =
  | InsightsDashboardTemplateChartKey
  | InsightsDashboardTemplateChartWithDefaults;

// Type guard to check if a chart entry has defaults
export function isChartWithDefaults(
  chart: InsightsDashboardTemplateChart
): chart is InsightsDashboardTemplateChartWithDefaults {
  return typeof chart === "object" && "chart" in chart && "defaults" in chart;
}

// Helper to get the chart key from a chart entry
export function getChartKey(
  chart: InsightsDashboardTemplateChart
): InsightsDashboardTemplateChartKey {
  return isChartWithDefaults(chart) ? chart.chart : chart;
}

export enum InsightsDashboardTemplateSvgIcon {
  AI = "svg-ai",
  Bar = "svg-bar",
  BarHorizontal = "svg-bar-horizontal",
  Line = "svg-line",
  Pie = "svg-pie",
}

export type InsightsDashboardTemplateIcon =
  | DesignSystemIcons
  | InsightsDashboardTemplateSvgIcon;

// Configuration field types - which inputs to show in configure step
export enum TemplateConfigField {
  DateRange = "dateRange",
  MediaOutreach = "mediaOutreach",
  MediaTypes = "mediaTypes",
  MentionStream = "mentionStream",
  SocialPlatforms = "socialPlatforms",
  SocialPost = "socialPost",
  SocialStream = "socialStream",
}

export interface InsightsDashboardTemplate {
  chartIconColor?: string;
  charts: InsightsDashboardTemplateChart[];
  configFields: TemplateConfigField[];
  // Description shown on the configure step (top left of modal)
  configureDescriptionKey: string;
  // Description shown in template details panel
  descriptionKey: string;
  disabledTooltipKey?: string;
  featureFlag?: FeatureKey;
  hasNewBadge?: boolean;
  icon: InsightsDashboardTemplateIcon;
  iconColorClass?: string;
  id: string;
  labelKey: string;
  modal?: string;
  requiredSections?: AppSection[];
}

// Configuration values from the configure template form
export interface InsightsDashboardTemplateConfig {
  aiVisibilityProjectId?: number;
  dateRange: DateRange;
  mediaOutreachIds?: number[];
  mentionStreamIds?: number[];
  mentionTypes?: MentionType[];
  socialMentionTypes?: SocialMentionType[];
  socialPostId?: number;
  socialStreamIds?: number[];
}

// Modal step state
export enum InsightsDashboardTemplateModalStep {
  Configure = "configure",
  Select = "select",
}

const EARNED_MEDIA_INSIGHTS_TEMPLATE: InsightsDashboardTemplate = {
  charts: [
    "mention_stream_insights_summary",
    "mention_benchmark_timeline",
    "coverage_volume_and_potential_audience_reach_timeline",
    "coverage_by_media_type",
    {
      chart: "top_articles_table",
      defaults: {
        label_key:
          "insights_dashboard_templates.chart_labels.top_articles_audience",
        sort_by: TopArticlesMetric.Audience,
      },
    },
    {
      chart: "top_articles_table",
      defaults: {
        label_key:
          "insights_dashboard_templates.chart_labels.top_articles_advertisement_rate",
        sort_by: TopArticlesMetric.AdvertisementRate,
      },
    },
    "total_social_shares",
    "themes",
    "leading_brands",
    "leading_print_sources",
    "leading_online_sources",
    "leading_print_journalists",
    "leading_online_journalists",
    "leading_tv_programs",
    "sentiment_breakdown",
    "facebook_engagement_metric",
    "domain_authority",
    "share_of_voice",
  ],
  configFields: [
    TemplateConfigField.MentionStream,
    TemplateConfigField.DateRange,
  ],
  configureDescriptionKey:
    "insights_dashboard_templates.earned_media_insights.configure_description",
  descriptionKey:
    "insights_dashboard_templates.earned_media_insights.description",
  icon: "mention-streams",
  iconColorClass: "tw:text-blue-700",
  id: "earned_media_insights",
  labelKey: "insights_dashboard_templates.earned_media_insights.name",
  requiredSections: [AppSection.MONITORING],
};

const BRAND_BENCHMARK_TEMPLATE: InsightsDashboardTemplate = {
  charts: [
    "mention_stream_insights_summary",
    "mentions_benchmark",
    {
      chart: "share_of_voice_timeline",
      defaults: {
        chart_variant: ChartVariant.StackedVerticalBar,
        label_key:
          "insights_dashboard_templates.chart_labels.sov_timeline_stacked_bar",
      },
    },
    {
      chart: "share_of_voice_timeline",
      defaults: {
        chart_variant: ChartVariant.Line,
        label_key:
          "insights_dashboard_templates.chart_labels.sov_timeline_line",
      },
    },
    {
      chart: "share_of_voice",
      defaults: {
        label_key: "insights_dashboard_templates.chart_labels.sov_mentions",
        sov_metric_type: SovMetricType.Mentions,
      },
    },
    {
      chart: "share_of_voice",
      defaults: {
        label_key:
          "insights_dashboard_templates.chart_labels.sov_audience_reach",
        sov_metric_type: SovMetricType.AudienceReach,
      },
    },
    {
      chart: "share_of_voice",
      defaults: {
        label_key: "insights_dashboard_templates.chart_labels.sov_ave",
        sov_metric_type: SovMetricType.Ave,
      },
    },
    {
      chart: "share_of_voice",
      defaults: {
        label_key:
          "insights_dashboard_templates.chart_labels.sov_social_shares",
        sov_metric_type: SovMetricType.SocialShares,
      },
    },
    "sentiment_benchmark",
  ],
  configFields: [
    TemplateConfigField.MentionStream,
    TemplateConfigField.DateRange,
  ],
  configureDescriptionKey:
    "insights_dashboard_templates.brand_benchmark.configure_description",
  descriptionKey: "insights_dashboard_templates.brand_benchmark.description",
  icon: InsightsDashboardTemplateSvgIcon.Line,
  iconColorClass: "tw:text-blue-700",
  id: "brand_benchmark",
  labelKey: "insights_dashboard_templates.brand_benchmark.name",
  requiredSections: [AppSection.MONITORING],
};

const OUTREACH_CAMPAIGN_PERFORMANCE_TEMPLATE: InsightsDashboardTemplate = {
  charts: [
    "outreach_insights_summary",
    "mention_stream_insights_summary",
    "outreach_coverage_timeline",
    "key_messages",
    "outreach_top_engaged_contacts_table",
    "outreach_performance_table",
    "outreach_activity_timeline",
    {
      chart: "top_articles_table",
      defaults: {
        label_key:
          "insights_dashboard_templates.chart_labels.top_articles_audience",
        sort_by: TopArticlesMetric.Audience,
      },
    },
    {
      chart: "top_articles_table",
      defaults: {
        label_key:
          "insights_dashboard_templates.chart_labels.top_articles_advertisement_rate",
        sort_by: TopArticlesMetric.AdvertisementRate,
      },
    },
    "total_social_shares",
    "domain_authority",
    "facebook_engagement_metric",
    "desktop_vs_mobile_readership",
    "sentiment_breakdown",
    "coverage_volume_and_potential_audience_reach_timeline",
    "geographic_spread_city_chart",
    "geographic_spread_state_chart",
  ],
  configFields: [
    TemplateConfigField.MentionStream,
    TemplateConfigField.MediaOutreach,
    TemplateConfigField.DateRange,
  ],
  configureDescriptionKey:
    "insights_dashboard_templates.outreach_campaign_performance.configure_description",
  descriptionKey:
    "insights_dashboard_templates.outreach_campaign_performance.description",
  icon: "outreach",
  iconColorClass: "tw:text-blue-700",
  id: "outreach_campaign_performance",
  labelKey: "insights_dashboard_templates.outreach_campaign_performance.name",
  requiredSections: [AppSection.OUTREACH],
};

const SOCIAL_INSIGHTS_TEMPLATE: InsightsDashboardTemplate = {
  charts: [
    "social_insights_summary",
    "coverage_volume_timeline",
    "leading_hashtags",
    "emoji_count",
    "top_facebook_posts",
    "top_facebook_pages",
    "top_bluesky_posts",
    "top_instagram_posts",
    "top_linkedin_posts",
    "top_tiktok_videos",
    "top_tweets",
    "top_youtube_videos",
    "key_bluesky_influencers",
    "key_twitter_influencers",
    "key_instagram_influencers",
    "key_youtube_influencers",
    "coverage_volume_breakdown",
    "sentiment_breakdown",
    "share_of_voice",
  ],
  configFields: [
    TemplateConfigField.SocialStream,
    TemplateConfigField.DateRange,
  ],
  configureDescriptionKey:
    "insights_dashboard_templates.social_insights.configure_description",
  descriptionKey: "insights_dashboard_templates.social_insights.description",
  icon: "social-streams",
  iconColorClass: "tw:text-purple-500",
  id: "social_insights",
  labelKey: "insights_dashboard_templates.social_insights.name",
  requiredSections: [AppSection.SOCIAL],
};

const EARNED_SOCIAL_INSIGHTS_TEMPLATE: InsightsDashboardTemplate = {
  charts: [
    "mention_stream_insights_summary",
    "social_insights_summary",
    "stream_vs_stream",
    "key_messages",
    "coverage_volume_breakdown",
    "coverage_by_media_type",
    "themes",
    "leading_hashtags",
    "sentiment_breakdown",
    "sentiment_benchmark",
    "top_tweets",
    "top_facebook_pages",
    "top_facebook_posts",
    "top_bluesky_posts",
    "top_instagram_posts",
    "top_linkedin_posts",
    "top_tiktok_videos",
    "top_youtube_videos",
    "key_bluesky_influencers",
    "key_twitter_influencers",
    "key_instagram_influencers",
    "key_youtube_influencers",
    "emoji_count",
    "coverage_volume_and_stock_price",
  ],
  configFields: [
    TemplateConfigField.MentionStream,
    TemplateConfigField.SocialStream,
    TemplateConfigField.DateRange,
  ],
  configureDescriptionKey:
    "insights_dashboard_templates.earned_social_insights.configure_description",
  descriptionKey:
    "insights_dashboard_templates.earned_social_insights.description",
  icon: InsightsDashboardTemplateSvgIcon.BarHorizontal,
  iconColorClass: "tw:text-blue-700",
  id: "earned_social_insights",
  labelKey: "insights_dashboard_templates.earned_social_insights.name",
  requiredSections: [AppSection.MONITORING, AppSection.SOCIAL],
};

const SOCIAL_CAMPAIGN_PERFORMANCE_TEMPLATE: InsightsDashboardTemplate = {
  charts: [
    "social_insights_summary",
    "social_engage_performance",
    "top_performing_post",
    "social_post_highlight",
    "social_post_highlight",
    "social_post_highlight",
    "coverage_volume_timeline",
    "coverage_volume_breakdown",
    "key_messages",
    "leading_hashtags",
    "sentiment_breakdown",
    "sentiment_timeline",
    "top_facebook_pages",
    "top_facebook_posts",
    "top_bluesky_posts",
    "top_instagram_posts",
    "top_linkedin_posts",
    "top_tiktok_videos",
    "top_youtube_videos",
    "key_bluesky_influencers",
    "key_twitter_influencers",
    "key_instagram_influencers",
    "key_youtube_influencers",
    "top_tweets",
    "emoji_count",
    "coverage_volume_and_analytics_timeline",
  ],
  configFields: [
    TemplateConfigField.SocialStream,
    TemplateConfigField.DateRange,
    TemplateConfigField.SocialPost,
  ],
  configureDescriptionKey:
    "insights_dashboard_templates.social_campaign_performance.configure_description",
  descriptionKey:
    "insights_dashboard_templates.social_campaign_performance.description",
  icon: InsightsDashboardTemplateSvgIcon.Bar,
  iconColorClass: "tw:text-blue-700",
  id: "social_campaign_performance",
  labelKey: "insights_dashboard_templates.social_campaign_performance.name",
  requiredSections: [AppSection.SOCIAL],
};

const UNIFIED_MEDIA_CAMPAIGN_TEMPLATE: InsightsDashboardTemplate = {
  charts: [
    "mention_stream_insights_summary",
    "outreach_insights_summary",
    "social_insights_summary",
    "social_engage_performance",
    "social_post_highlight",
    "top_performing_post",
    "outreach_performance_table",
    "outreach_top_engaged_contacts_table",
    {
      chart: "top_articles_table",
      defaults: {
        label_key:
          "insights_dashboard_templates.chart_labels.top_articles_audience",
        sort_by: TopArticlesMetric.Audience,
      },
    },
    {
      chart: "top_articles_table",
      defaults: {
        label_key:
          "insights_dashboard_templates.chart_labels.top_articles_advertisement_rate",
        sort_by: TopArticlesMetric.AdvertisementRate,
      },
    },
    "outreach_coverage_timeline",
    "stream_vs_stream",
    "coverage_by_media_type",
    "coverage_volume_breakdown",
    "themes",
    "leading_hashtags",
    "total_social_shares",
    "facebook_engagement_metric",
    "domain_authority",
    "sentiment_breakdown",
    "sentiment_timeline",
    "coverage_volume_and_analytics_timeline",
    "coverage_volume_and_referral_traffic_timeline",
  ],
  configFields: [
    TemplateConfigField.MentionStream,
    TemplateConfigField.SocialStream,
    TemplateConfigField.MediaOutreach,
    TemplateConfigField.DateRange,
    TemplateConfigField.SocialPost,
  ],
  configureDescriptionKey:
    "insights_dashboard_templates.unified_media_campaign.configure_description",
  descriptionKey:
    "insights_dashboard_templates.unified_media_campaign.description",
  icon: InsightsDashboardTemplateSvgIcon.Pie,
  iconColorClass: "tw:text-blue-700",
  id: "unified_media_campaign",
  labelKey: "insights_dashboard_templates.unified_media_campaign.name",
  requiredSections: [
    AppSection.MONITORING,
    AppSection.OUTREACH,
    AppSection.SOCIAL,
  ],
};

export const AI_VISIBILITY_TEMPLATE: InsightsDashboardTemplate = {
  chartIconColor: "tw:text-teal-600",
  charts: [
    "trajaan_word_cloud",
    "trajaan_visibility",
    "coverage_volume_timeline",
    "trajaan_top_authors",
    "trajaan_top_outlets",
    "trajaan_top_domains",
    "trajaan_sentiment",
    "share_of_voice",
    "share_of_voice_timeline",
    "outreach_coverage_timeline",
    "coverage_volume_and_stock_price",
  ],
  configFields: [],
  configureDescriptionKey:
    "insights_dashboard_templates.ai_visibility.description",
  descriptionKey: "insights_dashboard_templates.ai_visibility.description",
  disabledTooltipKey:
    "insights_dashboard_templates.ai_visibility.disabled_tooltip",
  hasNewBadge: true,
  icon: InsightsDashboardTemplateSvgIcon.AI,
  iconColorClass: "tw:text-teal-600",
  id: "ai_visibility",
  labelKey: "insights_dashboard_templates.ai_visibility.name",
  modal: "AIVisibilityModal",
  requiredSections: [AppSection.MONITORING, AppSection.AI_VISIBILITY],
};

export const INSIGHTS_DASHBOARD_TEMPLATES: InsightsDashboardTemplate[] = [
  EARNED_MEDIA_INSIGHTS_TEMPLATE,
  BRAND_BENCHMARK_TEMPLATE,
  OUTREACH_CAMPAIGN_PERFORMANCE_TEMPLATE,
  SOCIAL_INSIGHTS_TEMPLATE,
  EARNED_SOCIAL_INSIGHTS_TEMPLATE,
  UNIFIED_MEDIA_CAMPAIGN_TEMPLATE,
  SOCIAL_CAMPAIGN_PERFORMANCE_TEMPLATE,
  AI_VISIBILITY_TEMPLATE,
];
